import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import searchIcon from "../../assets/images/search.svg";

const Filter = ({
    onApply = () => {},
    filterOptions = [],
    initialFilters = {},
    onSectionSearch = () => {},
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filters, setFilters] = useState(initialFilters);
    const [activeSection, setActiveSection] = useState(null);
    const [searchTerms, setSearchTerms] = useState({});
    const filterRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target)
            ) {
                setIsOpen(false);
                setActiveSection(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        setFilters(initialFilters || {});
    }, [initialFilters]);

    const toggleSection = (sectionKey) => {
        setActiveSection(activeSection === sectionKey ? null : sectionKey);
    };

    const handleCheckboxChange = (sectionKey, optionValue) => {
        setFilters((prevFilters) => {
            const currentValues = prevFilters[sectionKey] || [];
            let newFilters;

            if (currentValues.includes(optionValue)) {
                newFilters = {
                    ...prevFilters,
                    [sectionKey]: currentValues.filter(
                        (value) => value !== optionValue
                    ),
                };
            } else {
                newFilters = {
                    ...prevFilters,
                    [sectionKey]: [...currentValues, optionValue],
                };
            }

            if (sectionKey === "university") {
                if (newFilters.course) {
                    newFilters = {
                        ...newFilters,
                        course: [],
                        specialization: [],
                        document_type: [],
                    };
                }

                onApply(newFilters);
            }

            if (sectionKey === "course") {
                if (newFilters.specialization) {
                    newFilters = {
                        ...newFilters,
                        specialization: [],
                        document_type: [],
                    };
                }

                onApply(newFilters);
            }

            if (sectionKey === "specialization") {
                if (newFilters.document_type) {
                    newFilters = {
                        ...newFilters,
                        document_type: [],
                    };
                }

                onApply(newFilters);
            }

            return newFilters;
        });
    };

    const handleSearch = (sectionKey, searchTerm) => {
        setSearchTerms((prev) => ({
            ...prev,
            [sectionKey]: searchTerm,
        }));

        if (searchTerm.length >= 2 || searchTerm.length === 0) {
            onSectionSearch(sectionKey, searchTerm);
        }
    };

    const handleDateRangeChange = (sectionKey, dateType, dateValue) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [sectionKey]: {
                ...prevFilters[sectionKey],
                [dateType]: dateValue,
            },
        }));

        const updatedFilters = {
            ...filters,
            [sectionKey]: {
                ...(filters[sectionKey] || {}),
                [dateType]: dateValue,
            },
        };

        onApply(updatedFilters);
    };

    const handleDateChange = (sectionKey, dateValue) => {
        setFilters((prevFilters) => ({
            ...prevFilters,
            [sectionKey]: dateValue,
        }));
        onApply({ ...filters, [sectionKey]: dateValue });
    };

    const clearAllFilters = () => {
        setFilters({});
        setSearchTerms({});

        onApply({});

        filterOptions.forEach((section) => {
            if (onSectionSearch) {
                onSectionSearch(section.key, "");
            }
        });
    };

    const applyFilters = () => {
        onApply(filters);
        setIsOpen(false);
    };

    const countActiveFilters = () => {
        let count = 0;

        Object.keys(filters).forEach((key) => {
            if (Array.isArray(filters[key])) {
                count += filters[key].length;
            } else if (
                typeof filters[key] === "object" &&
                filters[key] !== null
            ) {
                if (filters[key].from) count++;
                if (filters[key].to) count++;
            }
        });

        const hasSearchTerm = Object.values(searchTerms).some(
            (term) => term && term.length >= 2
        );

        return count + (hasSearchTerm ? 1 : 0);
    };

    const isSectionActive = (sectionKey) => {
        if (!filters[sectionKey]) return false;

        if (Array.isArray(filters[sectionKey])) {
            return filters[sectionKey].length > 0;
        } else if (typeof filters[sectionKey] === "object") {
            return filters[sectionKey].from || filters[sectionKey].to;
        }

        return searchTerms[sectionKey] && searchTerms[sectionKey].length >= 2;
    };

    return (
        <FilterContainer ref={filterRef}>
            <FilterButton onClick={() => setIsOpen(!isOpen)}>
                <span>Filter</span>
                {countActiveFilters() > 0 && (
                    <FilterCount>{countActiveFilters()}</FilterCount>
                )}
            </FilterButton>

            {isOpen && (
                <FilterPanel>
                    <FilterContent>
                        {filterOptions.map((section) => (
                            <FilterSection key={section.key}>
                                <SectionHeader
                                    isActive={activeSection === section.key}
                                    onClick={() => toggleSection(section.key)}
                                >
                                    <CheckboxContainer
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isSectionActive(section.key)) {
                                                setFilters((prev) => {
                                                    const newFilters = {
                                                        ...prev,
                                                    };
                                                    delete newFilters[
                                                        section.key
                                                    ];
                                                    return newFilters;
                                                });

                                                const newFilters = {
                                                    ...filters,
                                                };
                                                delete newFilters[section.key];
                                                onApply(newFilters);
                                            }
                                        }}
                                    >
                                        <StyledCheckbox
                                            type="checkbox"
                                            checked={isSectionActive(
                                                section.key
                                            )}
                                            readOnly
                                        />
                                    </CheckboxContainer>
                                    <span>{section.label}</span>
                                    <Icon
                                        isOpen={activeSection === section.key}
                                    />
                                </SectionHeader>

                                {activeSection === section.key && (
                                    <SectionContent>
                                        {section.type === "date" ? (
                                            <>
                                                <DateLabel>From:</DateLabel>
                                                <DateInput
                                                    type="date"
                                                    value={
                                                        filters[section.key]
                                                            ?.from || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleDateRangeChange(
                                                            section.key,
                                                            "from",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                                <DateLabel>To:</DateLabel>
                                                <DateInput
                                                    type="date"
                                                    value={
                                                        filters[section.key]
                                                            ?.to || ""
                                                    }
                                                    onChange={(e) =>
                                                        handleDateRangeChange(
                                                            section.key,
                                                            "to",
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <SearchBox>
                                                    <img
                                                        src={searchIcon}
                                                        alt="search"
                                                    />
                                                    <input
                                                        type="text"
                                                        placeholder={`Search for ${section.label.toLowerCase()}`}
                                                        value={
                                                            searchTerms[
                                                                section.key
                                                            ] || ""
                                                        }
                                                        onChange={(e) =>
                                                            handleSearch(
                                                                section.key,
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </SearchBox>
                                                <OptionsList>
                                                    {section.options.length >
                                                    0 ? (
                                                        section.options.map(
                                                            (option) => (
                                                                <OptionItem
                                                                    key={
                                                                        option.value
                                                                    }
                                                                >
                                                                    <CheckboxContainer>
                                                                        <StyledCheckbox
                                                                            type="checkbox"
                                                                            checked={(
                                                                                filters[
                                                                                    section
                                                                                        .key
                                                                                ] ||
                                                                                []
                                                                            ).includes(
                                                                                option.value
                                                                            )}
                                                                            onChange={() =>
                                                                                handleCheckboxChange(
                                                                                    section.key,
                                                                                    option.value
                                                                                )
                                                                            }
                                                                        />
                                                                    </CheckboxContainer>
                                                                    <OptionLabel>
                                                                        {
                                                                            option.label
                                                                        }
                                                                    </OptionLabel>
                                                                </OptionItem>
                                                            )
                                                        )
                                                    ) : (
                                                        <NoOptionsMessage>
                                                            No options found
                                                        </NoOptionsMessage>
                                                    )}
                                                </OptionsList>
                                            </>
                                        )}
                                    </SectionContent>
                                )}
                            </FilterSection>
                        ))}
                    </FilterContent>
                    <ActionButtons>
                        <ClearButton onClick={clearAllFilters}>
                            Clear All
                        </ClearButton>
                        <ApplyButton onClick={applyFilters}>Apply</ApplyButton>
                    </ActionButtons>
                </FilterPanel>
            )}
        </FilterContainer>
    );
};

export default Filter;

const FilterContainer = styled.div`
    position: relative;
    display: inline-block;
    min-width: 100px;
`;

const FilterButton = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: #f5f7fa;
    border: 1px solid #e9ecef;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #522e92;
    height: 40px;

    &:hover {
        background: #f0f2f5;
    }

    &::before {
        content: "";
        display: inline-block;
        width: 16px;
        height: 16px;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23522e92' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolygon points='22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3'%3E%3C/polygon%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
    }
`;

const FilterCount = styled.span`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: #522e92;
    color: white;
    border-radius: 50%;
    font-size: 12px;
    font-weight: 600;
`;

const FilterPanel = styled.div`
    position: absolute;
    top: calc(100% + 5px);
    right: 8px;
    width: 320px;
    background: white;
    border-radius: 10px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    padding: 16px;
    border: 1px solid #e3e8ef;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
`;

const FilterContent = styled.div`
    max-height: calc(80vh - 80px); /* Account for action buttons and padding */
    overflow-y: auto;
    margin-bottom: 16px;
    scrollbar-width: thin;
    scrollbar-color: #9dcbff #edf5fe;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: #edf5fe;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #9dcbff;
        border-radius: 4px;
    }
`;

const FilterSection = styled.div`
    margin-bottom: 12px;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    padding: 10px;
    cursor: pointer;
    border-radius: 8px;
    background-color: ${(props) =>
        props.isActive ? "#edf5fe" : "transparent"};
    border: 1px solid ${(props) => (props.isActive ? "#9dcbff" : "#e3e8ef")};
    color: #522e92;
    font-weight: 500;

    &:hover {
        background-color: ${(props) =>
            props.isActive ? "#edf5fe" : "#f8f9fa"};
    }
`;

const Icon = styled.span`
    margin-left: auto;
    border: solid #522e92;
    border-width: 0 2px 2px 0;
    display: inline-block;
    padding: 3px;
    transform: ${(props) =>
        props.isOpen ? "rotate(-135deg)" : "rotate(45deg)"};
    transition: transform 0.3s;
`;

const SectionContent = styled.div`
    padding: 12px;
    max-height: 250px;
    overflow: visible;
`;

const SearchBox = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 8px 10px;
    background-color: #f5f7fa;
    border-radius: 6px;
    margin-bottom: 8px;
    border: 1px solid #e9ecef;

    img {
        width: 14px;
        height: 14px;
        opacity: 0.7;
    }

    input {
        border: none;
        outline: none;
        background-color: transparent;
        font-size: 13px;
        width: 100%;
        color: #522e92;

        &::placeholder {
            color: #7b6294;
            opacity: 0.7;
        }
    }
`;

const OptionsList = styled.div`
    max-height: 150px;
    overflow-y: auto;
    margin-left: 4px;
    scrollbar-width: thin;
    scrollbar-color: #9dcbff #edf5fe;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-track {
        background: #edf5fe;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: #9dcbff;
        border-radius: 4px;
    }
`;

const OptionItem = styled.div`
    display: flex;
    align-items: center;
    padding: 6px 0;
    gap: 8px;
`;

const CheckboxContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
`;

const StyledCheckbox = styled.input`
    width: 18px;
    height: 18px;
    border-radius: 4px;
    accent-color: #522e92;
    cursor: pointer;
`;

const OptionLabel = styled.span`
    font-size: 14px;
    color: #333;
`;

const NoOptionsMessage = styled.div`
    padding: 10px 0;
    color: #888;
    font-size: 14px;
    text-align: center;
`;

const ActionButtons = styled.div`
    display: flex;
    justify-content: space-between;
    padding-top: 16px;
    border-top: 1px solid #e3e8ef;
    margin-top: auto;
`;

const ClearButton = styled.button`
    padding: 8px 16px;
    background: white;
    border: 1px solid #e3e8ef;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: #666;

    &:hover {
        background: #f8f9fa;
    }
`;

const ApplyButton = styled.button`
    padding: 8px 24px;
    background: #522e92;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 500;
    color: white;

    &:hover {
        background: #42277a;
    }
`;

const DateInput = styled.input`
    width: 100%;
    padding: 8px;
    margin: 5px 0;
    border: 1px solid #e3e8ef;
    border-radius: 5px;
    font-size: 14px;
    color: #522e92;
    background-color: #f5f7fa;

    &:focus {
        outline: none;
        border-color: #9dcbff;
        background-color: #edf5fe;
    }
`;

const DateLabel = styled.label`
    font-size: 14px;
    color: #522e92;
    margin-top: 10px;
`;
